import React, { useState, useEffect } from 'react';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import { styled } from '@mui/material/styles';
import Security from '@mui/icons-material/Security';
import School from '@mui/icons-material/School';
import TrendingUp from '@mui/icons-material/TrendingUp';
import Close from '@mui/icons-material/Close';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Schedule from '@mui/icons-material/Schedule';
import Group from '@mui/icons-material/Group';
import Star from '@mui/icons-material/Star';
import BookingForm from './BookingForm';
import InternRegistrationForm from './InternRegistrationForm';
import { getColleges } from '../services/api';
import photos from '../data/photos';
import { getActiveMentors } from '../data/mentors';
import { useNavigate, useLocation } from 'react-router-dom';
import { navigateAndScroll } from '../utils/navigation';
import {
  HOME_ASSESSMENT_PRICE, BUSINESS_ASSESSMENT_PRICE,
} from '../utils/constants';
import SEO from './common/SEO';

const HeroSection = styled(Box)({
  backgroundImage: `linear-gradient(rgba(30,58,138,0.82), rgba(124,58,237,0.82)), url("${photos.heroes.landing}")`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  color: 'white',
  position: 'relative',
  overflow: 'hidden',
  padding: '80px 0 100px',
});

const ValueCard = styled(Card)({
  height: '100%',
  textAlign: 'center',
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
  },
});

const PriceCard = styled(Paper)({
  padding: '40px 32px',
  textAlign: 'center',
  borderRadius: 20,
  border: '2px solid #7c3aed',
  position: 'relative',
  maxWidth: 420,
  margin: '0 auto',
});

const StepCard = styled(Paper)({
  padding: '32px 24px',
  textAlign: 'center',
  height: '100%',
  borderRadius: 16,
});

const StepNumber = styled(Box)({
  width: 60,
  height: 60,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '1.5rem',
  fontWeight: 'bold',
  margin: '0 auto 16px',
  boxShadow: '0 4px 15px rgba(124, 58, 237, 0.3)',
});

const pricingFeatures = [
  'Full 1-Hour On-Site Security Assessment',
  '15-Minute Personalized Consultation',
  'Network & WiFi Security Audit',
  'Device & IoT Security Check',
  'Data Backup & Sharing Review',
  'Expert-Reviewed Security Report',
  'Actionable Improvement Plan',
];

const howItWorksSteps = [
  { number: 1, title: 'Book Online', photo: photos.landing.stepBook, description: 'Choose a date, time, and college. Pay securely online. Home assessments from $249.99, business from $599.99.' },
  { number: 2, title: 'Meet Your Intern', photo: photos.landing.stepMeet, description: 'A vetted cybersecurity student arrives at your home or office for a 1-hour assessment.' },
  { number: 3, title: 'Expert Review', photo: photos.landing.stepReview, description: 'A certified cybersecurity professional reviews every finding before your report is finalized.' },
  { number: 4, title: 'Get Wise', photo: photos.landing.stepReport, description: 'Receive your expert-approved security report with a prioritized action plan. Know exactly what to fix and in what order.' },
];

function LandingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [bookingOpen, setBookingOpen] = useState(false);
  const [internOpen, setInternOpen] = useState(false);
  const [colleges, setColleges] = useState([]);
  const mentorData = getActiveMentors();

  useEffect(() => {
    let cancelled = false;
    async function loadColleges() {
      try {
        const data = await getColleges();
        if (!cancelled && data && data.colleges) {
          setColleges(data.colleges);
        }
      } catch {
        // Colleges will be empty; BookingForm handles gracefully
      }
    }
    loadColleges();
    return () => { cancelled = true; };
  }, []);

  return (
    <Box>
      <SEO description="Expert-reviewed cybersecurity assessments by supervised college interns. Home assessments from $249.99. Business assessments from $599.99." />

      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <Chip
            label="Trusted Cybersecurity Assessments"
            sx={{
              mb: 3,
              backgroundColor: 'rgba(255,255,255,0.15)',
              color: 'white',
              fontWeight: 600,
            }}
          />
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              mb: 2,
              fontSize: { xs: '2rem', sm: '2.75rem', md: '3.5rem' },
              lineHeight: 1.15,
            }}
          >
            Cybersecurity Assessments{' '}
            <Box component="span" sx={{ display: { xs: 'inline', md: 'block' } }}>
              Backed by Certified Experts
            </Box>
          </Typography>
          <Typography
            variant="h6"
            sx={{ mb: 2, opacity: 0.95, fontWeight: 400, maxWidth: 650, mx: 'auto' }}
          >
            Get your home or small business assessed by vetted student assessors,
            with every report reviewed and validated by CISSP-certified security experts.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mb: 3 }}>
            {['NIST-Based Framework', 'CISSP Expert Review', 'Report in 48 Hours', `From $${HOME_ASSESSMENT_PRICE.toFixed(0)}`].map(badge => (
              <Chip key={badge} label={badge} size="small"
                sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600, backdropFilter: 'blur(4px)' }} />
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => setBookingOpen(true)}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                backgroundColor: '#fbbf24',
                color: '#1e1e1e',
                fontWeight: 700,
                border: '2px solid #fbbf24',
                boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
                '&:hover': { backgroundColor: '#f59e0b', border: '2px solid #f59e0b', boxShadow: '0 6px 25px rgba(0,0,0,0.5)' },
              }}
            >
              {`Book Assessment - $${HOME_ASSESSMENT_PRICE.toFixed(2)}`}
            </Button>
          </Box>
        </Container>
      </HeroSection>

      {/* Trust Indicators */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 3 }}>
        <Container maxWidth="lg">
          <Grid container spacing={2} justifyContent="center" alignItems="center">
            {[
              { icon: <Star fontSize="small" />, text: '10+ Years Expert Experience' },
              { icon: <TrendingUp fontSize="small" />, text: '$1000 Assessment Value' },
              { icon: <Security fontSize="small" />, text: '100% Expert Reviewed' },
              { icon: <Schedule fontSize="small" />, text: '1 Hour Quick Assessment' },
            ].map((item) => (
              <Grid item xs={6} md={3} key={item.text}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <Box sx={{ color: '#7c3aed' }}>{item.icon}</Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>
                    {item.text}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Value Proposition Cards */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ textAlign: 'center', mb: 1, fontWeight: 700 }}>
            Why Choose Cyber Wise Corp?
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ textAlign: 'center', mb: 6, maxWidth: 600, mx: 'auto', fontWeight: 400 }}
          >
            We bridge the gap between enterprise security and everyday awareness.
          </Typography>
          <Grid container spacing={4}>
            {[
              {
                photo: photos.landing.expertWisdom,
                title: 'Expert-Supervised Assessments',
                description:
                  'Every assessment is performed by vetted student assessors and reviewed by CISSP-certified experts before delivery. You get expert-level oversight at an accessible price.',
              },
              {
                photo: photos.landing.empowerStudents,
                title: 'Next-Gen Talent Pipeline',
                description:
                  'Our assessors are senior-level college cybersecurity students, rigorously vetted and operating under structured frameworks. Your assessment fee directly invests in the future of cybersecurity.',
              },
              {
                photo: photos.landing.smartInvestment,
                title: 'Smart Investment',
                description:
                  'Professional cybersecurity assessments typically cost $1,000+. Our supervised model delivers structured, expert-reviewed assessments at a fraction of traditional consulting prices.',
              },
            ].map((card) => (
              <Grid item xs={12} md={4} key={card.title}>
                <ValueCard elevation={0} variant="outlined">
                  <Box sx={{ width: '100%', height: 200, overflow: 'hidden' }}>
                    <Box component="img" src={card.photo} alt={card.title}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Box>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
                      {card.title}
                    </Typography>
                    <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>
                      {card.description}
                    </Typography>
                  </CardContent>
                </ValueCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Why Trust CWC Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" sx={{ textAlign: 'center', mb: 1, fontWeight: 700 }}>
          Why Trust Cyber Wise Corp?
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ textAlign: 'center', mb: 5, maxWidth: 650, mx: 'auto', fontWeight: 400 }}>
          Every assessment is built on industry standards and validated by certified professionals.
        </Typography>
        <Grid container spacing={3}>
          {[
            { title: 'NIST-Based Framework', desc: 'Our assessment methodology is built on the NIST Cybersecurity Framework and CIS Critical Security Controls, the same standards used by Fortune 500 companies and government agencies.' },
            { title: 'CISSP Expert Review', desc: 'Every single assessment is reviewed by a cybersecurity professional holding the CISSP certification, the gold standard requiring 5+ years of hands-on experience.' },
            { title: 'Structured Methodology', desc: 'No guesswork. Our interns follow a standardized, scored checklist covering network security, device protection, data security, and user awareness.' },
            { title: 'Vetted Student Assessors', desc: 'Every student assessor passes rigorous screening: 80% minimum on our technical assessment, instructor referral, identity verification, and ongoing expert mentorship.' },
          ].map((item, i) => (
            <Grid item xs={12} sm={6} key={i}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <CheckCircle sx={{ color: '#16a34a', mt: 0.5, flexShrink: 0 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>{item.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>{item.desc}</Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button variant="outlined" size="large"
              href="/downloads/CWC-Sample-Assessment-Report.pdf" target="_blank"
              sx={{ fontWeight: 600, px: 4 }}>
              Sample Home Report (PDF)
            </Button>
            <Button variant="outlined" size="large"
              href="/downloads/CWC-Sample-Business-Assessment-Report.pdf" target="_blank"
              sx={{ fontWeight: 600, px: 4 }}>
              Sample Business Report (PDF)
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            See exactly what you receive after your assessment
          </Typography>
        </Box>
      </Container>

      {/* Pricing Section */}
      <Box sx={{ py: 10, backgroundColor: '#f8fafc' }}>
        <Container maxWidth="md">
          <Typography variant="h3" sx={{ textAlign: 'center', mb: 1, fontWeight: 700 }}>
            Simple, Transparent Pricing
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ textAlign: 'center', mb: 2, fontWeight: 400 }}
          >
            Flat rates. No hidden fees. No surprises. Every assessment is expert-reviewed before delivery.
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} sm={6} md={5}>
              <PriceCard elevation={3}>
                <Chip label="Home" color="primary" size="small"
                  sx={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', fontWeight: 700 }} />
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>Home Assessment</Typography>
                <Typography variant="h2" sx={{ fontWeight: 800, color: '#1e3a8a', mb: 0.5 }}>
                  ${HOME_ASSESSMENT_PRICE.toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Perfect for homes and home offices</Typography>
                <Box sx={{ textAlign: 'left', mb: 3 }}>
                  {['1-hour on-site assessment', 'Network, device & IoT audit', '15-minute consultation', 'Expert-reviewed report', 'CWC security certificate'].map((f) => (
                    <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                      <CheckCircle sx={{ color: '#16a34a', fontSize: 20 }} />
                      <Typography variant="body2">{f}</Typography>
                    </Box>
                  ))}
                </Box>
                <Button variant="contained" size="large" fullWidth onClick={() => setBookingOpen(true)}
                  sx={{ py: 1.5, fontSize: '1rem' }}>Book Home Assessment</Button>
                <Button variant="text" size="small" fullWidth
                  onClick={() => navigateAndScroll(navigate, '/home-assessment', location.pathname)}
                  sx={{ mt: 1, color: 'text.secondary', textTransform: 'none', fontSize: '0.85rem' }}>
                  Learn more about what's included
                </Button>
              </PriceCard>
            </Grid>
            <Grid item xs={12} sm={6} md={5}>
              <PriceCard elevation={3}>
                <Chip label="Business" color="secondary" size="small"
                  sx={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', fontWeight: 700 }} />
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>Small Business Assessment</Typography>
                <Typography variant="h2" sx={{ fontWeight: 800, color: '#7c3aed', mb: 0.5 }}>
                  ${BUSINESS_ASSESSMENT_PRICE.toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>2-hour on-site | 10 or fewer employees</Typography>
                <Box sx={{ textAlign: 'left', mb: 3 }}>
                  {['2-hour on-site assessment', 'Everything in home +', 'POS & business system audit', 'Employee access review', 'Policy & compliance check'].map((f) => (
                    <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                      <CheckCircle sx={{ color: '#16a34a', fontSize: 20 }} />
                      <Typography variant="body2">{f}</Typography>
                    </Box>
                  ))}
                </Box>
                <Button variant="contained" size="large" fullWidth onClick={() => setBookingOpen(true)}
                  sx={{ py: 1.5, fontSize: '1rem', background: 'linear-gradient(135deg, #7c3aed, #5b21b6)' }}>Book Business Assessment</Button>
                <Button variant="text" size="small" fullWidth
                  onClick={() => navigateAndScroll(navigate, '/business-assessment', location.pathname)}
                  sx={{ mt: 1, color: 'text.secondary', textTransform: 'none', fontSize: '0.85rem' }}>
                  Learn more about what's included
                </Button>
              </PriceCard>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Why This Model Works */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography variant="h3" sx={{ textAlign: 'center', mb: 1, fontWeight: 700 }}>
          Why This Model Works
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ textAlign: 'center', mb: 5, fontWeight: 400 }}>
          A win for everyone involved
        </Typography>
        <Grid container spacing={3}>
          {[
            { emoji: '\uD83C\uDF93', title: 'Students', desc: 'Gain real-world cybersecurity experience, earn competitive pay, and build a professional portfolio that employers value more than classroom projects alone.' },
            { emoji: '\uD83C\uDFE0', title: 'Homeowners & Businesses', desc: 'Get professional, expert-reviewed cybersecurity assessments at a price that makes sense. Know exactly where your vulnerabilities are and what to fix first.' },
            { emoji: '\uD83C\uDFEB', title: 'Colleges', desc: 'Strengthen cybersecurity programs with a direct pipeline from classroom to field experience. Students graduate career-ready, not just degree-ready.' },
            { emoji: '\uD83C\uDFD8\uFE0F', title: 'Communities', desc: 'Every assessment makes a home or business more secure. As more households get assessed, entire neighborhoods become harder targets for cybercriminals.' },
            { emoji: '\uD83C\uDDFA\uD83C\uDDF8', title: 'The U.S. Workforce', desc: 'America faces a critical shortage of cybersecurity professionals. CWC is building the pipeline by giving students the one thing they cannot get in a classroom: field experience.' },
          ].map((item, i) => (
            <Grid item xs={12} sm={6} key={i}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Typography variant="h4" sx={{ flexShrink: 0 }}>{item.emoji}</Typography>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>{item.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>{item.desc}</Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Meet Your Mentors */}
      <Box sx={{ backgroundColor: 'grey.50', py: 10 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ textAlign: 'center', mb: 1, fontWeight: 700 }}>
            Meet Your Expert Reviewers
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ textAlign: 'center', mb: 6, fontWeight: 400 }}>
            Students perform the field assessment. Certified experts validate every final report before delivery.
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            {mentorData.map((mentor) => (
              <Grid item xs={12} sm={6} md={4} key={mentor.id}>
                <Card sx={{ height: '100%', transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                  '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 10px 30px rgba(0,0,0,0.15)' } }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Box component="img" src={mentor.photo} alt={mentor.name}
                        sx={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover',
                          border: '3px solid', borderColor: 'secondary.main',
                          boxShadow: '0 4px 15px rgba(124, 58, 237, 0.3)', flexShrink: 0 }} />
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{mentor.name}</Typography>
                        <Typography variant="caption" color="secondary.main" sx={{ fontWeight: 600 }}>{mentor.title}</Typography>
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, mb: 2 }}>
                      {mentor.bio}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                      {mentor.topCerts.map((cert) => (
                        <Chip key={cert} label={cert} size="small"
                          sx={{ fontWeight: 600, backgroundColor: 'rgba(124,58,237,0.1)', color: 'secondary.dark' }} />
                      ))}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {mentor.highlights.map((h, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                          <Typography variant="body2">{h}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4, fontStyle: 'italic' }}>
            Supported by a growing panel of experienced cybersecurity reviewers. Assessment methodology aligned with NIST and CIS Controls frameworks. Every report is reviewed. No exceptions.
          </Typography>
        </Container>
      </Box>

      {/* How It Works */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ textAlign: 'center', mb: 1, fontWeight: 700 }}>
            How Cyber Wise Works
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ textAlign: 'center', mb: 1, fontWeight: 400 }}
          >
            Four simple steps to a more secure home or business.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 6 }}>
            Assessments are performed by trained cybersecurity students and reviewed by CISSP-certified professionals.
          </Typography>
          <Grid container spacing={4}>
            {howItWorksSteps.map((step) => (
              <Grid item xs={12} sm={6} md={3} key={step.number}>
                <StepCard elevation={0} variant="outlined">
                  <Box component="img" src={step.photo} alt={step.title}
                    sx={{ width: 120, height: 120, borderRadius: '50%', objectFit: 'cover', mb: 2, mx: 'auto', display: 'block',
                      boxShadow: '0 4px 15px rgba(124, 58, 237, 0.3)' }} />
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {step.description}
                  </Typography>
                </StepCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Bottom CTA */}
      <Box
        sx={{
          py: 10,
          backgroundImage: `linear-gradient(rgba(30,58,138,0.85), rgba(124,58,237,0.85)), url("${photos.heroes.about}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" sx={{ fontWeight: 800, mb: 2 }}>
            Ready to Get Wise?
          </Typography>
          <Typography variant="h6" sx={{ mb: 5, opacity: 0.85, fontWeight: 400 }}>
            Know where you stand. Book an affordable, expert-reviewed security assessment today.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => setBookingOpen(true)}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                backgroundColor: '#fbbf24',
                color: '#1e1e1e',
                fontWeight: 700,
                border: '2px solid #fbbf24',
                boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
                '&:hover': { backgroundColor: '#f59e0b', border: '2px solid #f59e0b', boxShadow: '0 6px 25px rgba(0,0,0,0.5)' },
              }}
            >
              {`Book Assessment - $${HOME_ASSESSMENT_PRICE.toFixed(2)}`}
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Booking Dialog */}
      <Dialog
        open={bookingOpen}
        onClose={() => setBookingOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Book Your Assessment
          </Typography>
          <IconButton onClick={() => setBookingOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <BookingForm
            colleges={colleges}
            onSuccess={() => {
              setTimeout(() => setBookingOpen(false), 3000);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Intern Registration Dialog */}
      <Dialog
        open={internOpen}
        onClose={() => setInternOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Join the CWC Team
          </Typography>
          <IconButton onClick={() => setInternOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <InternRegistrationForm
            colleges={colleges}
            onSuccess={() => {
              setTimeout(() => setInternOpen(false), 3000);
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default LandingPage;
